import type { SortingState } from '@tanstack/react-table';
import { useMemo } from 'react';
import { Actions } from '../../components/product-table/columns/actions-column';
import { DOH } from '../../components/product-table/columns/doh-column';
import { ItemCode } from '../../components/product-table/columns/item-code-column';
import { ProductNameWithLabels } from '../../components/product-table/columns/product-name-with-labels-column';
import { QOH } from '../../components/product-table/columns/qoh-column';
import { Reorder } from '../../components/product-table/columns/reorder-column';
import { getUnitsSoldColumn } from '../../components/product-table/columns/sales-columns';
import { Size } from '../../components/product-table/columns/size-column';
import { Type } from '../../components/product-table/columns/type-column';
import { VendorName } from '../../components/product-table/columns/vendor-name-column';
import { DepartmentFilter } from '../../components/product-table/filters/department-filter';
import { FiltersPopover } from '../../components/product-table/filters/filters-popover';
import { HideDisabledFilter } from '../../components/product-table/filters/hide-disabled-filter';
import { SoldInFilter } from '../../components/product-table/filters/sold-in-filter';
import { VendorNameFilter } from '../../components/product-table/filters/vendor-name-filter';
import type { StrictColumnDef } from '../../components/tanstack-table/column';
import { ReportActions } from '../../components/tanstack-table/report-actions';
import { TanStackTable } from '../../components/tanstack-table/table';
import { useRouteRowSelection } from '../../components/tanstack-table/use-route-row-selection';
import { useTableRef } from '../../components/tanstack-table/use-table-ref';
import { PageToolbar } from '../../components/toolbar/page-toolbar';
import { SearchWithCount } from '../../components/toolbar/search-with-count';
import { ProductTableContext } from '../../contexts/product-table-context';
import type { Product } from '../../data/product';
import { type Filters, useFilteredProducts } from '../../hooks/products';
import { productReportsLayout } from '../../router';

const defaultFilters: Filters = {
  maximumDOH: 5,
  hideDisabled: true,
  soldIn: 90,
};

export function LowStockReport() {
  const products = productReportsLayout.useLoaderData();
  const { filters, setFilters, filteredProducts, baselineCount } = useFilteredProducts(
    products,
    defaultFilters
  );
  const { rowSelection, onRowSelectionChange, onRowClick } = useRouteRowSelection();
  const [table, onTableReady] = useTableRef<Product>();

  const unitsSoldColumn = useMemo(() => getUnitsSoldColumn(filters.soldIn || 90), [filters.soldIn]);

  const columns = useMemo<StrictColumnDef<Product>[]>(() => {
    return [
      VendorName,
      ItemCode,
      ProductNameWithLabels,
      Type,
      Size,
      QOH,
      DOH,
      unitsSoldColumn,
      Reorder,
      Actions,
    ];
  }, [unitsSoldColumn]);

  // Multi-column sort: VendorName ascending, then unitsSold descending
  const sorting = useMemo<SortingState>(
    () => [
      { id: VendorName.id, desc: false },
      { id: unitsSoldColumn.id, desc: true },
    ],
    [unitsSoldColumn.id]
  );

  return (
    <>
      <ProductTableContext.Provider value={{ filters, setFilters, filteredProducts }}>
        <PageToolbar
          left={
            <FiltersPopover defaultFilters={defaultFilters}>
              <HideDisabledFilter />
              <SoldInFilter />
              <VendorNameFilter products={products} />
              <DepartmentFilter products={products} />
            </FiltersPopover>
          }
          right={
            <>
              <SearchWithCount
                quantity={filteredProducts.length}
                total={baselineCount}
                noun="products"
              />
              <ReportActions table={table} title="Low Stock" />
            </>
          }
        />
      </ProductTableContext.Provider>
      <TanStackTable
        data={filteredProducts}
        columns={columns}
        sorting={sorting}
        rowSelection={rowSelection}
        onRowSelectionChange={onRowSelectionChange}
        onRowClick={onRowClick}
        getRowId={(product) => product.id}
        onTableReady={onTableReady}
      />
    </>
  );
}
